import { orderBy } from "lodash";
import { useMemo, useState } from "react";
import { Sort, SortDirection } from "../modules/search/@types";

/**
 * Client side sort when we receive all the data from backend
 * @param initialData - Initialization data - array of items
 * @param initialSort - Initialization sort
 * @param optionalMapping - object that contains optional mapping for orderBy function. This is used in case that some data properties are differently named
 *                          from table column names used to indicate sort field
 *
 */
const useClientSort = <T>(
  initialData?: Array<T>,
  initialSort?: Sort,
  optionalMapping?: Record<
    string,
    Array<string> | ((value: T) => Array<string | number> | number | string)
  >
) => {
  const [sort, setSort] = useState<Sort | undefined>(initialSort);

  // Derived rather than stored, so the sort survives new data and applies from the first render.
  const data = useMemo(
    () =>
      sort
        ? orderBy(initialData, optionalMapping?.[sort.field] ?? [sort.field], [
            sort.direction
          ])
        : initialData,
    [initialData, optionalMapping, sort]
  );

  const toggleSort = (field: string, firstDirection: SortDirection = "asc") =>
    setSort(prev => {
      if (prev?.field !== field) return { field, direction: firstDirection };
      return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
    });

  return { sort, data, onSortChange: setSort, toggleSort };
};

export default useClientSort;
